import { useState, FormEvent } from 'react';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-quote-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message || 'No message provided',
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-charcoal relative overflow-hidden">
      {/* Wave Pattern Overlay */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <pattern id="wave" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M0 50 Q 25 30, 50 50 T 100 50" stroke="white" fill="none" strokeWidth="2" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#wave)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-20 md:py-24 relative z-10">
        <div className="grid md:grid-cols-5 gap-12 md:gap-16">
          {/* Form Area - 60% */}
          <div className="md:col-span-3">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Get a Quote</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all"
                />

                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all"
                />

                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all"
                />

                <input
                  type="text"
                  name="address"
                  placeholder="Neighborhood / Address (Optional)"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all"
                />

                <select
                  name="service"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  required
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all cursor-pointer"
                >
                  <option value="">Select Service Needed</option>
                  <option value="Pumps & Motors">Pumps & Motors</option>
                  <option value="Filters">Filters</option>
                  <option value="Water Treatment">Water Treatment</option>
                  <option value="Heaters">Heaters</option>
                  <option value="Pool Lights">Pool Lights</option>
                  <option value="Leak Detection">Leak Detection</option>
                  <option value="Coping">Coping</option>
                  <option value="Renovations">Renovations</option>
                  <option value="Diamond Brite">Diamond Brite</option>
                  <option value="Repairs">General Repairs</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Other">Other</option>
                </select>

                <textarea
                  name="message"
                  placeholder="Tell us about your pool needs..."
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3.5 rounded-lg bg-white border border-gray-300 focus:border-tropical-teal focus:outline-none focus:ring-2 focus:ring-tropical-teal/20 transition-all resize-vertical"
                />
                <div className="text-gray-400 text-sm text-right">
                  {formData.message.length}/500 characters
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-coral-pink text-white py-4 rounded-full font-bold text-lg hover:bg-opacity-90 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Request Quote'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-100 px-4 py-3 rounded-lg">
                    Thank you! We'll contact you soon. Check your email at migueljralanso@gmail.com for the quote details.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg">
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Contact Info - 40% */}
          <div className="md:col-span-2 flex flex-col justify-start pt-0 md:pt-16">
            <div className="space-y-8">
              <div>
                <div className="text-gray-400 text-sm mb-2">Call/Text:</div>
                <a
                  href="tel:+17862738796"
                  className="text-white text-3xl md:text-4xl font-bold hover:text-tropical-teal transition-colors flex items-center gap-3 cursor-pointer"
                >
                  <i className="ri-phone-fill text-tropical-teal"></i>
                  +1 (786) 273-8796
                </a>
              </div>

              <div>
                <div className="text-gray-400 text-sm mb-2">Email:</div>
                <a
                  href="mailto:migueljralanso@gmail.com"
                  className="text-white text-xl md:text-2xl font-semibold hover:text-tropical-teal transition-colors flex items-center gap-3 cursor-pointer break-all"
                >
                  <i className="ri-mail-fill text-tropical-teal"></i>
                  migueljralanso@gmail.com
                </a>
              </div>

              <div>
                <div className="text-gray-400 text-sm mb-2">Service Area:</div>
                <div className="text-white text-xl flex items-center gap-2">
                  <i className="ri-map-pin-fill text-tropical-teal"></i>
                  Miami, FL
                </div>
              </div>
            </div>

            {/* Decorative Palm Tree */}
            <div className="hidden md:block mt-12 opacity-10">
              <i className="ri-plant-line text-white text-9xl"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}